import { useEditMode, useEnumOptionFilter, useVisualBinding } from "@judo/core";
import type { EnumerationCombo, EnumerationRadio, EnumerationToggleButtonbar, Input } from "@judo/model-api";
import { BinaryInputComponent } from "./BinaryInputComponent";
import { CheckboxComponent } from "./CheckboxComponent";
import { DateInputComponent } from "./DateInputComponent";
import { DateTimeInputComponent } from "./DateTimeInputComponent";
import { EnumerationComboComponent } from "./EnumerationComboComponent";
import { EnumerationRadioComponent } from "./EnumerationRadioComponent";
import { EnumerationToggleButtonbarComponent } from "./EnumerationToggleButtonbarComponent";
import { NumericInputComponent } from "./NumericInputComponent";
import { PasswordInputComponent } from "./PasswordInputComponent";
import { SwitchComponent } from "./SwitchComponent";
import { TextAreaComponent } from "./TextAreaComponent";
import { TextInputComponent } from "./TextInputComponent";
import { TimeInputComponent } from "./TimeInputComponent";

/**
 * Converts model Option[] to component options format.
 * Uses enumerationMember.name as value and label/name as display.
 */
function convertEnumOptions(
	element: EnumerationCombo | EnumerationRadio | EnumerationToggleButtonbar
): Array<{ value: string; label: string }> {
	return (element.options ?? []).map((opt) => ({
		value: opt.enumerationMember?.name ?? opt.name,
		label: opt.label ?? opt.enumerationMember?.name ?? opt.name,
	}));
}

export interface InputRendererProps {
	element: Input;
	readOnly?: boolean;
}

/**
 * Dispatcher to specific input components based on element type.
 * Connects data binding via useVisualBinding hook.
 */
export function InputRenderer({ element, readOnly }: InputRendererProps) {
	// Connect to data store via visual binding
	const binding = useVisualBinding(element);

	// Enum option filter (called unconditionally per rules of hooks)
	const enumOptionFilter = useEnumOptionFilter(element["xmi:id"]);

	// Edit mode for JUDO-viewMode CSS class
	const { isEditMode } = useEditMode();

	// If element is hidden (via hiddenBy, static hidden, or customization override), don't render
	if (binding.hidden) {
		return null;
	}

	// Merge readOnly from props, element, and binding
	const isReadOnly = readOnly ?? binding.readOnly ?? element.isReadOnly ?? false;

	// Build effective element with visual property overrides applied.
	// binding.label already includes visual property override from VisualPropertiesProvider.
	// For required: merge binding.required (includes visual override) with element-level required
	// (element.required || attributeType.isRequired) for data-level 1..1 cardinality.
	const elementRequired = element.required === true || element.attributeType?.isRequired === true;
	const effectiveRequired = binding.required || elementRequired;
	const effectiveElement = {
		...element,
		label: binding.label || element.label,
		required: effectiveRequired,
	};

	// Build JUDO CSS className for template parity
	const judoClassName = [!isEditMode && "JUDO-viewMode", effectiveRequired && "JUDO-required"]
		.filter(Boolean)
		.join(" ");

	// Common props from binding
	const commonProps = {
		value: binding.value as any, // Type depends on input type, will be validated by component
		onChange: binding.onChange,
		error: binding.error ?? undefined,
		disabled: binding.disabled,
		judoClassName,
	};

	// Dispatch to specific input component
	switch (element["@type"]) {
		case "TextInput":
			return <TextInputComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;

		case "NumericInput":
			return <NumericInputComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;

		case "DateInput":
			return <DateInputComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;

		case "DateTimeInput":
			return <DateTimeInputComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;

		case "TimeInput":
			return <TimeInputComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;

		case "TextArea":
			return <TextAreaComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;

		case "Checkbox":
			return <CheckboxComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;

		case "Switch":
			return <SwitchComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;

		case "EnumerationCombo": {
			const enumElement = effectiveElement as EnumerationCombo;
			// Nullable when not required (either by visual element or underlying attribute)
			const isNullable = !enumElement.required && !enumElement.attributeType?.isRequired;
			const comboOptions = convertEnumOptions(enumElement);
			return (
				<EnumerationComboComponent
					element={effectiveElement}
					readOnly={isReadOnly}
					options={enumOptionFilter ? enumOptionFilter(comboOptions) : comboOptions}
					nullable={isNullable}
					{...commonProps}
				/>
			);
		}

		case "EnumerationRadio": {
			const enumElement = effectiveElement as EnumerationRadio;
			// Nullable when not required (either by visual element or underlying attribute)
			const isNullable = !enumElement.required && !enumElement.attributeType?.isRequired;
			// Orientation based on stretch property: HORIZONTAL stretch = horizontal layout
			const orientation =
				enumElement.stretch === "HORIZONTAL" || enumElement.stretch === "BOTH" ? "horizontal" : "vertical";
			const radioOptions = convertEnumOptions(enumElement);
			return (
				<EnumerationRadioComponent
					element={effectiveElement}
					readOnly={isReadOnly}
					options={enumOptionFilter ? enumOptionFilter(radioOptions) : radioOptions}
					nullable={isNullable}
					orientation={orientation}
					{...commonProps}
				/>
			);
		}

		case "EnumerationToggleButtonbar": {
			const enumElement = effectiveElement as EnumerationToggleButtonbar;
			// Nullable when not required (either by visual element or underlying attribute)
			const isNullable = !enumElement.required && !enumElement.attributeType?.isRequired;
			// Orientation based on stretch property: HORIZONTAL stretch = horizontal layout
			const orientation = enumElement.stretch === "VERTICAL" ? "vertical" : "horizontal";
			const toggleOptions = convertEnumOptions(enumElement);
			return (
				<EnumerationToggleButtonbarComponent
					element={effectiveElement}
					readOnly={isReadOnly}
					options={enumOptionFilter ? enumOptionFilter(toggleOptions) : toggleOptions}
					nullable={isNullable}
					orientation={orientation}
					{...commonProps}
				/>
			);
		}

		case "BinaryTypeInput":
			return <BinaryInputComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;

		case "PasswordInput":
			return <PasswordInputComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;

		default:
			// Fallback to text input
			return <TextInputComponent element={effectiveElement} readOnly={isReadOnly} {...commonProps} />;
	}
}
